import { useCallback, useRef, useState } from 'react'
import { FileUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatBytes } from '@/lib/format'
import { useI18n, type DictParams } from '@/lib/i18n'

interface FileUploaderProps {
  /** Allowed file extensions, e.g. ['.mp4', '.mov']. Empty array accepts everything. */
  accept: readonly string[]
  /** Maximum file size in megabytes. */
  maxSizeMB: number
  /** Allow picking multiple files (ZIP compression). */
  multiple?: boolean
  /** Called only with a valid file (single-file mode). */
  onFileSelect?: (file: File) => void
  /** Called only when every picked file is valid (multi-file mode). */
  onFilesSelect?: (files: File[]) => void
  className?: string
}

/** Drag & drop / click-to-browse file picker with type and size validation. */
export function FileUploader({
  accept,
  maxSizeMB,
  multiple = false,
  onFileSelect,
  onFilesSelect,
  className,
}: FileUploaderProps) {
  const [dragging, setDragging] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Drag counter guards against flicker from drag events on child elements.
  const dragDepth = useRef(0)
  const { t } = useI18n()

  const validateFile = useCallback(
    (file: File): string | null => {
      const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`
      if (accept.length > 0 && !accept.includes(ext)) {
        return t('upInvalidType', {
          ext,
          accept: accept.join(', '),
        } satisfies DictParams)
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        return t('upTooLarge', {
          name: file.name,
          maxSize: formatBytes(maxSizeMB * 1024 * 1024),
        } satisfies DictParams)
      }
      return null
    },
    [accept, maxSizeMB, t],
  )

  const handleFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return

      setLocalError(null)
      const invalid = files.map((file) => validateFile(file)).find(Boolean)
      if (invalid) {
        setLocalError(invalid)
        return
      }

      if (multiple) {
        onFilesSelect?.(files)
      } else {
        const first = files[0]
        if (first) onFileSelect?.(first)
      }
    },
    [multiple, onFileSelect, onFilesSelect, validateFile],
  )

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      dragDepth.current = 0
      setDragging(false)
      handleFiles(Array.from(event.dataTransfer.files))
    },
    [handleFiles],
  )

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click()
      }}
      onDragEnter={(event) => {
        event.preventDefault()
        dragDepth.current += 1
        setDragging(true)
      }}
      onDragLeave={(event) => {
        event.preventDefault()
        dragDepth.current -= 1
        if (dragDepth.current <= 0) setDragging(false)
      }}
      onDragOver={(event) => event.preventDefault()}
      onDrop={onDrop}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed bg-card px-6 py-14 text-center shadow-sm transition-colors duration-150 ease-out',
        'hover:border-primary/50 hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        dragging && 'border-primary bg-accent/50',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        multiple={multiple}
        accept={accept.join(',')}
        onChange={(event) => {
          handleFiles(Array.from(event.target.files ?? []))
          event.target.value = ''
        }}
      />

      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <FileUp className="h-7 w-7" aria-hidden />
      </span>

      <div className="space-y-1">
        <p className="text-base font-medium">
          {dragging ? t('upDrop') : t('upDrag')}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('upOr')} <span className="font-medium text-primary">{t('upBrowse')}</span>{' '}
          {t('upFromDevice')}
        </p>
      </div>

      <p className="text-xs text-muted-foreground">
        {accept.length > 0
          ? t('upLimit', { formats: accept.join(' · '), maxSizeMB })
          : t('upLimitPerFile', { maxSizeMB })}
      </p>

      {localError && (
        <p className="max-w-sm text-sm font-medium text-destructive">{localError}</p>
      )}
    </div>
  )
}